// ═══ 거래처 스코프 목록 패널 (리빌드 R3) — 정산 탭 온디맨드 픽커 본문 ═══
// 정본 UX: erp/app/docs/settlement-ledger/rebuild-agent-guide.md §3 R3 / zero-base-design.md §6.1
//
// ClientScopeCombo 와 동일한 props 계약 { data, value, onChange } — 상주 배치를 걷어내고(Jin 8/12)
// 오버레이 픽커 안에서 쓴다(fluid). 데이터 가공/스코프 인코딩(a:/u:)은 콤보와 같은 원천을 쓴다.
//
// 동작 계약:
//  - 상단 검색: 하위 업체까지 평면 매칭 + '이름 · 그룹명' 꼬리표, keyup 엔터 = 결과 1건이면 즉시 선택 + blur
//  - 목록: (allRow 시) 👥 전체 거래처 → 브로커 계정 행(이름 + N곳 + 미수, ▸/▾ 토글 — 기본 접힘)
//          → 펼치면 하위 들여쓰기(개별 스코프) → 직거래처 평면
//  - 행 클릭 = 스코프 선택(계정 행은 취합 스코프), 토글 클릭 = 펼침만 (선택 안 함)
//  - 목록 스크롤은 패널 자체(maxHeight) — 우측 장부 표와 독립. 잔액 0 도 표기.
//  - 표시 금액은 호출자가 넘긴 값 그대로 — 2026-08-11 부터 '확정 미수'(잔액 − 이번 달 청구).
import React, { useState } from 'react';
import { won, balColor, flattenScopeOptions } from './ClientScopeCombo';
import { useIsMobile } from '../utils/useMobile';

var panelSt = { width: 230, flexShrink: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: 'fit-content', maxHeight: 'calc(100vh - 160px)' };
var headSt = { padding: '12px 14px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc' };
var titleSt = { fontSize: 13, fontWeight: 700, color: '#1B3A5C', marginBottom: 8 };
var searchSt = { width: '100%', padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, outline: 'none', boxSizing: 'border-box' };
var listSt = { overflowY: 'auto', flex: 1 };
var rowSt = { display: 'flex', alignItems: 'center', gap: 6, padding: '9px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9' };
var nameSt = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
var tailSt = { fontSize: 11, color: '#94a3b8', flexShrink: 0 };
var amtSt = { marginLeft: 'auto', fontSize: 11, whiteSpace: 'nowrap', flexShrink: 0 };
var emptySt = { padding: 12, color: '#94a3b8', fontSize: 12 };
// 펼친 하위 업체 컨테이너 — 그룹 헤더 아래 들여쓴 카드 (직거래처 평면 행과 명확히 구분)
var unitsBoxSt = { margin: '4px 8px 8px 22px', border: '1px solid #bfdbfe', borderLeft: '3px solid #60a5fa', borderRadius: 8, overflow: 'hidden', background: '#f5f9ff' };
var unitRowInnerSt = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', cursor: 'pointer', fontSize: 12.5, color: '#334155', borderBottom: '1px solid #e4eefb' };

function ClientScopePanel(props) {
  // 모바일(≤768px) — 폭 계약(fluid=100% / drawer=전고)은 그대로, 행 터치 타깃만 키운다.
  var isMobile = useIsMobile();
  var rowStM = isMobile ? Object.assign({}, rowSt, { padding: '12px' }) : rowSt;
  var unitRowInnerStM = isMobile ? Object.assign({}, unitRowInnerSt, { padding: '11px 10px' }) : unitRowInnerSt;
  var searchStM = isMobile ? Object.assign({}, searchSt, { padding: '10px', fontSize: 13 }) : searchSt;
  var data = props.data || {};
  var loading = !!props.loading;
  var groups = Array.isArray(data.groups) ? data.groups : [];
  var directs = Array.isArray(data.directs) ? data.directs : [];
  var value = props.value || '';
  var onChange = props.onChange || function () {};
  // fluid — 픽커 안에서 폭을 컨테이너에 맞춘다. drawer — 우측 드로어 전고 채움.
  var boxSt = props.fluid ? Object.assign({}, panelSt, { width: '100%', maxHeight: '72vh' }) : panelSt;
  if (props.drawer) boxSt = Object.assign({}, boxSt, { height: '100%', maxHeight: '100%', borderRadius: 0, border: 'none' });
  var [q, setQ] = useState('');
  var [openMap, setOpenMap] = useState({}); // {accountId: bool} — 명시 토글만 기록 (미기록 = 기본 접힘)

  var pick = function (v) {
    setQ('');
    onChange(v);
  };

  var ql = q.trim().toLowerCase();
  // 검색어 일치 구간 하이라이트 (빨간 글자, Jin 8/12)
  var hiName=function(name){
    var n=String(name||'');
    if(!ql)return n;
    var i=n.toLowerCase().indexOf(ql);
    if(i<0)return n;
    return (<span>{n.slice(0,i)}<span style={{color:'#dc2626',fontWeight:700}}>{n.slice(i,i+ql.length)}</span>{n.slice(i+ql.length)}</span>);
  };

  var filtered = ql
    ? flattenScopeOptions(groups, directs).filter(function (o) {
        return o.name.toLowerCase().indexOf(ql) !== -1 || (o.group || '').toLowerCase().indexOf(ql) !== -1;
      })
    : [];

  // 선택된 하위 업체가 든 그룹은 기본 펼침 (드릴다운 직후 현재 위치가 보이도록). 명시 토글이 우선.
  var selUnit = value.indexOf('u:') === 0 ? value.slice(2) : '';
  var isOpen = function (acc, units) {
    if (openMap[acc.id] !== undefined) return !!openMap[acc.id];
    return units.some(function (u) { return String(u.id) === selUnit; });
  };
  var toggle = function (acc, units) {
    var next = !isOpen(acc, units);
    setOpenMap(function (prev) {
      var n = Object.assign({}, prev);
      n[acc.id] = next;
      return n;
    });
  };

  var amountCell = function (bal) {
    return <span style={Object.assign({ color: balColor(bal) }, amtSt)}>{won(bal)}</span>;
  };
  var rowStyle = function (v, isGroup) {
    var on = v === value;
    return Object.assign({}, rowStM, {
      background: on ? (isGroup ? '#dbeafe' : '#eff6ff') : (isGroup ? '#f8fafc' : '#fff'),
      fontWeight: on ? 700 : (isGroup ? 600 : 400),
      color: isGroup ? '#1e3a5f' : '#334155'
    });
  };

  return (<div style={boxSt}>
    <div style={headSt}>
      <div style={titleSt}>거래처</div>
      <input
        autoFocus={!!props.autoFocus}
        value={q}
        onChange={function (e) { setQ(e.target.value); }}
        onKeyUp={function (e) {
          // keyup 기준 — 한글 IME 조합 확정 엔터도 잡는다 (콤보와 동일 규약)
          if (e.key !== 'Enter') return;
          if (ql && filtered.length === 1) pick(filtered[0].v);
          e.currentTarget.blur();
        }}
        placeholder="거래처 검색..."
        style={searchStM} />
    </div>

    <div style={listSt}>
      {/* 검색 중 — 그룹 구조 대신 평면 결과 + '이름 · 그룹명' 꼬리표 */}
      {!!ql && filtered.map(function (o) {
        return (<div key={o.v} style={rowStyle(o.v, o.kind === 'group')} onClick={function () { pick(o.v); }}>
          <span style={nameSt}>{hiName(o.name)}</span>
          {o.kind === 'group'
            ? <span style={tailSt}>{o.unitsCount}곳</span>
            : (o.group ? <span style={tailSt}>· {o.group}</span> : null)}
          {amountCell(o.balance)}
        </div>);
      })}
      {!!ql && filtered.length === 0 && <div style={emptySt}>검색 결과 없음</div>}

      {/* 목록(검색어 없음) — 맨 위 '전체 거래처'(대상 축의 '' 값). 화면 전환은 헤더 세그먼트가 담당한다 (IA v2) */}
      {!ql && !!props.allRow &&
        <div style={rowStyle('', true)} onClick={function () { pick(''); }}>
          <span style={{ width: 18, flexShrink: 0 }} />
          <span style={nameSt}>👥 전체 거래처</span>
        </div>}

      {!ql && groups.map(function (g) {
        var acc = (g && g.account) || {};
        var units = (g && g.units) || [];
        var gv = 'a:' + acc.id;
        var open = isOpen(acc, units);
        return (<div key={gv}>
          <div style={rowStyle(gv, true)} onClick={function () { pick(gv); }}>
            <span style={nameSt}>{acc.name}</span>
            {/* 펼침/접힘 — 라벨 있는 필 버튼 (▸ 만으로는 접힘 상태를 이해하기 어렵다는 피드백, Jin 8/12) */}
            <button type="button" title={open ? '하위 업체 접기' : '하위 업체 펼치기'}
              onClick={function (e) { e.stopPropagation(); toggle(acc, units); }}
              style={{ flexShrink: 0, border: '1px solid #bfdbfe', background: open ? '#dbeafe' : '#fff', color: '#2563eb', borderRadius: 999, padding: isMobile ? '8px 10px' : '2px 9px', fontSize: 11, fontWeight: 700, cursor: 'pointer', lineHeight: 1.4 }}>
              {(acc.unitsCount || units.length) + '곳 ' + (open ? '접기 ▴' : '펼치기 ▾')}
            </button>
            {amountCell(acc.balance)}
          </div>
          {/* 하위 업체 — 들여쓴 중첩 카드 블록(파란 레일+테두리+틴트)으로 직거래처와 명확히 구분 */}
          {!!open && <div style={unitsBoxSt}>
            {units.map(function (u) {
              var uv = 'u:' + u.id;
              var on = uv === value;
              return (<div key={uv}
                style={Object.assign({}, unitRowInnerStM, on ? { background: '#dbeafe', fontWeight: 700 } : {})}
                onClick={function () { pick(uv); }}>
                <span style={nameSt}>{u.name}</span>
                {amountCell(u.balance)}
              </div>);
            })}
            {units.length === 0 && <div style={Object.assign({}, emptySt, { padding: '8px 10px' })}>하위 업체 없음</div>}
          </div>}
        </div>);
      })}

      {!ql && directs.map(function (d) {
        var dv = d.unitId ? 'u:' + d.unitId : 'a:' + d.accountId; // 단위 없는 직거래 계정(예: 백필 (미지정)) 은 계정 스코프로
        return (<div key={dv} style={rowStyle(dv, false)} onClick={function () { pick(dv); }}>
          <span style={{ width: 18, flexShrink: 0 }} />
          <span style={nameSt}>{d.name}</span>
          {amountCell(d.balance)}
        </div>);
      })}

      {!ql && groups.length === 0 && directs.length === 0 &&
        <div style={emptySt}>{loading ? '불러오는 중…' : '등록된 거래처가 없습니다'}</div>}
    </div>
  </div>);
}

export default ClientScopePanel;
